import { isNil } from 'lodash-es';
import { propIs } from 'utils';

import {
  ICartEntry,
  ICombo,
  IComboSlot,
  IComboSlotOption,
  IItem,
  IModifierSelection,
  IPrices,
  IQuantityBasedPlu,
} from '@rbi-ctg/menu';
import { CartEntryType } from 'utils/cart/types';
import logger from 'utils/logger';
import { assignInclusionComboSlotSelections, isCombo, isItem } from 'utils/menu';
import { IComboSlotSelectionsNoDataInSelections } from 'utils/menu/price';
import {
  IWithVendorConfig,
  PluTypes,
  PosVendors,
  bestPlusForQuantity,
  computeCompositeComboSlotPlu,
  concatenateSizePlu,
  getConstantPlu,
  getVendorConfig,
} from 'utils/vendor-config';
import { getDefaultSelectionsForComboSlot } from 'utils/wizard';

import {
  IPriceForComboSlotSelectionFn,
  IPriceForItemOptionModifierFn,
  ISelectionsIncluded,
  IsCartEntryType,
} from './types';

export const isCartEntryCombo = () => propIs<ICartEntry>('type', CartEntryType.combo);

export const isCartEntryWithTypeCombo = isCartEntryCombo() as IsCartEntryType<ICartEntry>;

/**
 * hack - this is an attempt to reconcile bad data in
 * the ui layer - there is a chance that injection will
 * fail if the price for a plu is not found, but this
 * is a temporary fix to display a derived price.
 * if minPlu.quantity is not 1 this may be a bad price.
 * it will only be used for display - orders must be validated
 * and priced before injection.
 **/
const priceForQuantityPlu = (
  item: IWithVendorConfig,
  quantity: number = 1,
  prices: IPrices,
  vendor: PosVendors | null
) => {
  const vendorConfig = getVendorConfig(item, vendor) as IQuantityBasedPlu;
  if (!vendorConfig || !prices) {
    return 0;
  }
  const plus = bestPlusForQuantity(vendorConfig.quantityBasedPlu, prices, quantity);
  if (!plus.length) {
    return 0;
  }
  return plus.reduce((acc, { plu }) => acc + prices[plu], 0);
};

const getPriceForPlus = (
  plu: string,
  mainItemPlu: string | null = null,
  prices: IPrices
): number => {
  const combinedPlu = `${plu}-${mainItemPlu}`;

  if (mainItemPlu && prices && !isNil(prices[combinedPlu])) {
    return prices[combinedPlu];
  }

  return prices?.[plu] || 0;
};

/**
 * Returns the price of a menu object without any selections
 */
export const pricingFunctionNoSelections = (
  item: IWithVendorConfig,
  prices: IPrices,
  vendor: PosVendors | null,
  quantity?: number
): number => {
  const vendorConfig = getVendorConfig(item, vendor);
  if (!vendorConfig || vendorConfig.pluType === PluTypes.IGNORE) {
    return 0;
  }

  // for quantity based plus: if price exists for plu, use it -
  // otherwise compute best price for quantity selected from
  // available plus
  // Note: if quantity is undefined we should allow
  // `priceForQuantityPlu` to find the minimum quantity
  // plu to use for pricing, so we don't default to 1
  // until after this check
  if (vendorConfig.pluType === PluTypes.QUANTITY) {
    return priceForQuantityPlu(item, quantity, prices, vendor);
  }

  const itemQuantity = quantity || 1;

  /**
   * If item is a combo, and there is a main item with a constant plu,
   * we want to check if there exists a price `<comboPlu>-<mainItemPlu>`,
   * and if it does, use that price instead of `<comboPlu>`
   */

  const mainItemComboPlu =
    isCombo(item) && item.mainItem ? getConstantPlu(getVendorConfig(item.mainItem, vendor)) : null;

  /**
   * This is due to repricing of cart entries.
   * The correct way would be to reprice everything based on requests from sanity,
   * and not count exclusively with the data from local storage.
   * This should be temporary fix.
   */
  const mainItemCartEntry =
    isCartEntryWithTypeCombo(item) &&
    item.cartId &&
    item.children.find(entry => entry.type === 'Item');

  const mainItemCartEntryPlu = mainItemCartEntry
    ? getConstantPlu(getVendorConfig(mainItemCartEntry, vendor))
    : null;

  const mainItemPlu = mainItemComboPlu || mainItemCartEntryPlu;

  if (vendorConfig.pluType === PluTypes.MULTI_CONSTANT) {
    return (
      (vendorConfig.multiConstantPlus || []).reduce(
        (acc, { plu }) => acc + getPriceForPlus(plu, mainItemPlu, prices),
        0
      ) * itemQuantity
    );
  }

  let price = 0;
  if (vendorConfig.pluType === PluTypes.CONSTANT) {
    price = getPriceForPlus(vendorConfig.constantPlu, mainItemPlu, prices);
  }

  if (vendorConfig.pluType === PluTypes.SIZE_BASED) {
    const plu = concatenateSizePlu(vendorConfig.sizeBasedPlu);
    price = plu ? getPriceForPlus(plu, mainItemPlu, prices) : 0;
  }

  if (vendorConfig.pluType === PluTypes.PARENT_CHILD) {
    price =
      getPriceForPlus(vendorConfig.parentChildPlu.plu, mainItemPlu, prices) +
      getPriceForPlus(vendorConfig.parentChildPlu.childPlu, mainItemPlu, prices);
  }

  return price * itemQuantity;
};

export const priceForPremiumComboSlotDelta = (
  comboSlot: IComboSlot,
  selectedItem: IItem,
  prices: IPrices,
  vendor: PosVendors | null
) => {
  const firstDefaultSelection = comboSlot.options?.length
    ? getDefaultSelectionsForComboSlot(comboSlot.options)[0]
    : undefined;

  // get price of default option
  const defaultPrice = firstDefaultSelection?.option
    ? pricingFunctionNoSelections(firstDefaultSelection?.option?.option, prices, vendor)
    : 0;
  // subtract the price of the default from the premiumOption
  const delta = pricingFunctionNoSelections(selectedItem, prices, vendor) - defaultPrice;
  // If delta is negative the price is zero
  return delta > 0 ? delta : 0;
};

export const priceForPremiumComboSlotComposite = (
  combo: ICombo,
  selectedItem: IItem,
  prices: IPrices,
  vendor: PosVendors | null
) => {
  const compositePlu = computeCompositeComboSlotPlu({
    parent: combo,
    child: selectedItem,
    vendor,
  });
  return compositePlu ? prices?.[compositePlu] ?? 0 : 0;
};

export const priceForItemModifierSelections = ({
  item,
  quantity = 1,
  modifierSelections = [],
  prices,
  priceForItemOptionModifier,
}: {
  item: IItem | null;
  quantity: number;
  modifierSelections: Array<IModifierSelection>;
  prices?: IPrices;
  priceForItemOptionModifier: IPriceForItemOptionModifierFn;
}): number => {
  if (!isItem(item)) {
    return 0;
  }

  return (
    (modifierSelections as Array<IModifierSelection>).reduce<number>(
      (price, { _key, modifier }) => {
        const itemOption = (item.options || []).find(
          ({ _key: itemOptionKey }) => _key === itemOptionKey
        );

        // illegal case, modifier selection
        // does not correspond to this item's
        // options. discard it when pricing
        if (!itemOption) {
          logger.warn({
            itemId: item._id,
            message: 'No ItemOption found for modifier',
            modifier,
          });
          return price;
        }

        const modifierPrice = priceForItemOptionModifier({
          item,
          itemOption,
          modifier,
          prices,
        });

        return price + modifierPrice;
      },
      0
    ) * quantity
  );
};

export const priceForComboSelections = ({
  combo,
  quantity = 1,
  modifierSelections = [],
  comboSlotSelections = {},
  prices,
  vendor,
  priceForItemOptionModifier,
  priceForComboSlotSelection,
}: {
  combo: ICombo | null;
  quantity: number;
  modifierSelections: Array<IModifierSelection>;
  comboSlotSelections: IComboSlotSelectionsNoDataInSelections;
  prices: IPrices;
  vendor: PosVendors | null;
  priceForItemOptionModifier: IPriceForItemOptionModifierFn;
  priceForComboSlotSelection: IPriceForComboSlotSelectionFn;
}): number => {
  if (!isCombo(combo)) {
    return 0;
  }

  const mainItemModsTotal: number = priceForItemModifierSelections({
    item: combo.mainItem,
    quantity: 1,
    modifierSelections: (modifierSelections || []).filter(({ comboSlotId }) => !comboSlotId),
    prices,
    priceForItemOptionModifier,
  });

  // price upsell combo slot items and combo slot modifiers
  const comboSlots = combo?.options || [];
  const comboSlotTotal = comboSlots.reduce<number>((acc, comboSlot) => {
    const { selections } = comboSlotSelections[comboSlot._id] || {};

    if (!selections) {
      return acc;
    }

    const assignedSelections: ISelectionsIncluded[] = assignInclusionComboSlotSelections(
      selections,
      comboSlot.maxAmount
    );

    const comboTotals: number = assignedSelections.reduce<number>((total, sel) => {
      const premiumPrice = sel.option.isPremium
        ? priceForComboSlotSelection({
            combo,
            comboSlot,
            selectedOption: selections.find(
              (selection: { option: IComboSlotOption }) =>
                selection.option.option._id === sel.option.option._id
            )?.option,
          })
        : 0;

      // the price for all selections not included in the combo (not part of the combo, went over comboSlots max)
      const upsellItemPrice = sel.notIncluded
        ? pricingFunctionNoSelections(sel.option.option, prices, vendor, sel.notIncluded)
        : premiumPrice;
      // the price for the selected modifiers
      const modifiersPrice = isItem(sel.option.option)
        ? priceForItemModifierSelections({
            item: sel.option.option,
            quantity: 1,
            modifierSelections: (modifierSelections || []).filter(
              ({ comboSlotId }) => comboSlotId === comboSlot._id
            ),
            prices,
            priceForItemOptionModifier,
          })
        : 0;

      return total + upsellItemPrice + modifiersPrice * sel.quantity;
    }, 0);

    return acc + comboTotals;
  }, 0);

  return (mainItemModsTotal + comboSlotTotal) * quantity;
};
